import { useContext, useMemo, useState } from 'react';
import { t } from '@sm/intl';
import { StaticContext } from '@sm/webassets';

import { STRIPE_ELIGIBLE_PACKAGES } from '~pricing/lib/constants';
import { Packages } from '~shared/constants/pricing';
import { usePackages } from '~shared/hooks/usePackages';
import { PricingPackage } from '~shared/types/pricing/pricingsvc';

import COPY from './cta_copy';
import useTrackingDetails from './useTrackingDetails';
import { CtaHook } from './types';

export default function useCta(pricingPackage: PricingPackage): CtaHook {
  const { user } = useContext(StaticContext);
  const { userPackage, showEduDiscount } = usePackages();
  const [isEnabled, setIsEnabled] = useState(true);
  const [isUserPackage, setIsUserPackage] = useState(false);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const { pageUtSource } = useTrackingDetails();

  useMemo(() => {
    // if we don't have the user's package details (because they're in a package that's
    // not part of the standard pricingweb loadout), default to allowing the CTA
    // TODO: at the server side, if the user's package is not returned in the pricingsvc
    // call, we need to do a separate fetch to retrieve it so we can reflect this CTA correctly
    const isUpgradeable = userPackage?.details ? userPackage.details.tier < pricingPackage.details.tier : true;
    const packageIsUserPackage = userPackage?.packageName === pricingPackage.packageName;
    const { isAuthenticated, isStripeEligible } = user;
    const useStripeCheckout = isStripeEligible && STRIPE_ELIGIBLE_PACKAGES.includes(pricingPackage.packageName);
    let newCtaUrl = '';

    if (packageIsUserPackage) {
      // Since all users start with a basic package, we check if they are authenticated or not
      // and set the correct cta label and sign up URL
      if (!isAuthenticated) {
        setCtaLabel(t(COPY.SIGN_UP));
        newCtaUrl = `/user/sign-up/?ut_source=${pageUtSource}`;
      } else {
        setCtaLabel(t(COPY.YOUR_PLAN));
      }
    } else if (isUpgradeable) {
      // Labels first; simpler logic
      if (pricingPackage.packageName === Packages.Enterprise) {
        setCtaLabel(t(COPY.CONTACT_US));
      } else {
        setCtaLabel(t(isAuthenticated ? COPY.SELECT : COPY.SIGN_UP));
      }

      // And URLs
      if (pricingPackage.packageName === Packages.Enterprise) {
        const utSource2 = pageUtSource?.includes('details') ? 'teams_details' : 'teams';
        newCtaUrl = `/mp/contact-sales/?ut_source=${pageUtSource}&ut_source2=${utSource2}&ut_source3=primary_contact_cta&package_id=${pricingPackage.id}`;
      } else if (showEduDiscount) {
        if (isAuthenticated && userPackage?.packageName === Packages.Basic) {
          newCtaUrl = `/billing/pw/discount/verify?package_id=${pricingPackage.id}&ut_source=${pageUtSource}`;
        } else if (isAuthenticated) {
          newCtaUrl = `/upgrade?package_id=${pricingPackage.id}&e=edu_faq&ut_source=${pageUtSource}`;
        } else {
          newCtaUrl = `/user/sign-up/?e=edu_faq&sm=${pricingPackage.encryptedEduParam}&ut_source=${pageUtSource}`;
        }
      } else if (isAuthenticated) {
        if (userPackage?.packageName === Packages.Basic) {
          newCtaUrl = `/billing/checkout?package_id=${pricingPackage.id}&ut_source=${pageUtSource}`;
        } else {
          newCtaUrl = `/upgrade?package_id=${pricingPackage.id}&ut_source=${pageUtSource}`;
        }
      } else {
        newCtaUrl = `/user/sign-up/?sm=${pricingPackage.encryptedCtaParam}&ut_source=${pageUtSource}`;
      }
    }

    // if this is good to go for stripe checkout, make the URL swap here
    // it's a pretty easy find/replace scenario on the path
    // GROW-2426: if its the user subscribed package and using stripe do not set the url
    // Setting the url renders a link, see https://code.corp.surveymonkey.com/wrench/wrench/blob/master/packages/button/Button.tsx
    if (useStripeCheckout && !packageIsUserPackage && !showEduDiscount) {
      newCtaUrl = `/checkout${newCtaUrl.replace('/billing/checkout', '').replace('/upgrade', '')}`;
    }

    setCtaUrl(newCtaUrl);
    setIsEnabled(!isAuthenticated || isUpgradeable);
    setIsUserPackage(packageIsUserPackage);
  }, [
    user,
    userPackage,
    pricingPackage,
    showEduDiscount,
    pageUtSource,
    setIsEnabled,
    setIsUserPackage,
    setCtaLabel,
    setCtaUrl,
  ]);

  return {
    isEnabled,
    isUserPackage,
    ctaUrl,
    ctaLabel,
  };
}
